/**
 * Attachment Picker Dialog
 * 
 * Generic attachment picker with 4 tabs:
 * - My Device: Upload files from device
 * - Library: Browse existing files
 * - Link: Create URL attachments
 * - Camera: Capture photo (if supported)
 * 
 * Follows ERPNext "Attach" dialog pattern but in React.
 * Completely generic - no doctype-specific logic.
 */

"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Upload, 
  FolderOpen, 
  Link as LinkIcon, 
  Camera, 
  X, 
  Check,
  File as FileIcon,
  Loader2,
  AlertCircle,
  Play
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { 
  useAttachments, 
  useAttachFile, 
  useCreateLinkAttachment, 
  useFileLibrary 
} from "@/lib/attachments/hooks";
import { erp } from "@/lib/erp";
import { uploadFile as uploadFileUnattached } from "@/lib/erp/files";
import { normalizeFileUrl } from "@/lib/attachments/utils";
import { toast } from "sonner";
import type { AttachmentPickerDialogProps } from "./types";
import type { FileLibraryItem, GetAttachmentsResponse } from "@/lib/attachments/types";

// Simple progress bar component
function Progress({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cn("w-full h-2 bg-muted rounded-full overflow-hidden", className)}>
      <div
        className="h-full bg-[#007AFF] transition-all duration-300 ease-out"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// Check if file is an image (by mime_type or extension)
function isImageFile(file: FileLibraryItem): boolean {
  // Primary: check mime_type (or file_type for backward compatibility)
  const mimeType = file.mime_type || (file as any).file_type;
  if (mimeType?.startsWith('image/')) {
    return true;
  }

  // Fallback: check file extension on file_name or file_url
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.ico'];
  const fileName = (file.file_name || '').toLowerCase();
  const fileUrl = (file.file_url || '').split('?')[0].toLowerCase(); // strip query params
  return imageExtensions.some(ext => fileName.endsWith(ext) || fileUrl.endsWith(ext));
}

// Check if file is a video (by mime_type or extension)
function isVideoFile(file: FileLibraryItem): boolean {
  // Primary: check mime_type (or file_type for backward compatibility)
  const mimeType = file.mime_type || (file as any).file_type;
  if (mimeType?.startsWith('video/')) {
    return true;
  }

  // Fallback: check file extension on file_name or file_url
  const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v', '.flv', '.wmv', '.3gp'];
  const fileName = (file.file_name || '').toLowerCase();
  const fileUrl = (file.file_url || '').split('?')[0].toLowerCase(); // strip query params
  return videoExtensions.some(ext => fileName.endsWith(ext) || fileUrl.endsWith(ext));
}

// Image component with icon fallback on error
function ImageWithFallback({ src, alt }: { src: string; alt: string }) {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <FileIcon className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }
  
  // Normalize absolute URLs to relative paths so they go through Vite proxy
  // This fixes host mismatches (localhost vs 127.0.0.1) and ensures auth cookies work
  const normalizedSrc = normalizeFileUrl(src) ?? src;
  
  return (
    <img
      src={normalizedSrc}
      alt={alt}
      className="w-full h-full object-cover"
      onError={() => setHasError(true)}
    />
  );
}

export function AttachmentPickerDialog({
  open,
  onOpenChange,
  context,
  multiple = false,
  accept,
  mode = 'attach',
  onCompleted,
  stagedFileUrls = [],
}: AttachmentPickerDialogProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'device' | 'library' | 'link' | 'camera'>('device');
  
  // My Device tab state
  const [uploadQueue, setUploadQueue] = useState<Array<{
    id: string;
    file: File;
    status: 'pending' | 'uploading' | 'done' | 'error';
    progress: number;
    error?: string;
    file_url?: string;
    file_name?: string;
  }>>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  
  // Library tab state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLibraryFiles, setSelectedLibraryFiles] = useState<Set<string>>(new Set());
  const debouncedSearch = useDebounce(searchQuery, 300);
  
  // Link tab state
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  
  // Camera tab state
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraSupported, setIsCameraSupported] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Hooks - only use attachment hooks if not in upload-only mode
  const isUploadOnly = mode === 'upload-only';
  const { data: attachmentsData } = useAttachments(context, { enabled: !isUploadOnly });
  const attachFileMutation = useAttachFile(context);
  const createLinkMutation = useCreateLinkAttachment(context);
  
  // Convert accept array to HTML accept string
  const acceptString = accept ? accept.join(",") : undefined;
  
  // Check camera support
  useEffect(() => {
    setIsCameraSupported(!!navigator.mediaDevices?.getUserMedia);
  }, []);
  
  // Check if device is touch (for drag & drop) - use state to avoid hydration mismatch
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      setIsTouchDevice(window.matchMedia("(pointer: coarse)").matches);
    }
  }, []);
  
  // Debug instrumentation - remove after fixing the issue
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[APD] open prop changed:', open);
      if (!open) {
        console.log('[APD] Dialog closing');
      }
    }
  }, [open]);
  
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[APD] Component mounted');
      return () => {
        console.log('[APD] ⚠️ Component UNMOUNTING - this indicates a React remount!');
        console.trace('[APD] Unmount stack trace');
      };
    }
  }, []);
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveTab('device');
      setUploadQueue([]);
      setSearchQuery("");
      setSelectedLibraryFiles(new Set());
      setUrl("");
      setLabel("");
      stopCamera();
    }
  }, [open]);
  
  // Library query - always query all files (no backend mime_type filter)
  // We'll filter on frontend based on accept prop for better reliability
  const { 
    data: libraryData, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage,
    isLoading: isLoadingLibrary
  } = useFileLibrary({
    query: debouncedSearch,
    mime_type: undefined, // Always query all files (like sidebar) - filter on frontend instead
    page_size: 20,
  }, {
    enabled: activeTab === 'library' && open,
  });
  
  // Flatten library pages
  const allLibraryFiles = libraryData?.pages.flatMap(page => page.items) || [];
  
  // Filter by accept prop on frontend (more reliable than backend file_type)
  // Also deduplicate by normalized file_url — the library can return multiple
  // File docs pointing to the same physical file (e.g., same file attached to
  // different documents). Without dedup, clicking one entry marks all duplicates
  // as "Attached", confusing users (OPS-1318).
  const libraryFiles = useMemo(() => {
    let filtered = allLibraryFiles;

    if (accept && accept.length > 0) {
      const imagesOnly = accept.some(a => a.includes('image'));
      const videosOnly = accept.some(a => a.includes('video'));

      if (imagesOnly && !videosOnly) {
        filtered = filtered.filter(file => isImageFile(file));
      } else if (videosOnly && !imagesOnly) {
        filtered = filtered.filter(file => isVideoFile(file));
      }
    }

    // Deduplicate by normalized file_url (keep first occurrence)
    const seen = new Set<string>();
    return filtered.filter(file => {
      const key = normalizeFileUrl(file.file_url) || file.file_url;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allLibraryFiles, accept]);
  
  // Check which files are already attached
  // In upload-only mode, use stagedFileUrls; otherwise use attachments from query
  const attachedFileUrls = useMemo(() => {
    if (isUploadOnly) {
      // Normalize staged file URLs for comparison
      return new Set(
        stagedFileUrls.map(url => normalizeFileUrl(url) || url).filter(Boolean)
      );
    }
    // Normal mode: use attachments from query
    return new Set(
      (attachmentsData?.attachments.map(a => normalizeFileUrl(a.file_url) || a.file_url) || []).filter(Boolean)
    );
  }, [isUploadOnly, stagedFileUrls, attachmentsData]);
  
  // Upload completion state
  const allUploadsComplete = uploadQueue.length > 0 && 
    uploadQueue.every(item => item.status === 'done' || item.status === 'error');
  const successfulUploads = uploadQueue.filter(item => item.status === 'done').length;
  const failedUploads = uploadQueue.filter(item => item.status === 'error').length;
  
  // ========== My Device Tab Handlers ==========
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isTouchDevice) {
      setIsDragging(true);
    }
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };
  
  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Validate files
    // Per-type size caps: videos can be large product demos, images stay lean.
    // Backend must mirror: Frappe site_config max_file_size + nginx client_max_body_size.
    const MAX_IMAGE_SIZE = 20 * 1024 * 1024;  // 20 MB
    const MAX_VIDEO_SIZE = 150 * 1024 * 1024; // 150 MB
    const getMaxSize = (file: File) => file.type.startsWith('video/') ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    const validFiles: File[] = [];
    const invalidFiles: Array<{ file: File; reason: string }> = [];

    Array.from(files).forEach(file => {
      // Check file size
      const maxSize = getMaxSize(file);
      if (file.size > maxSize) {
        invalidFiles.push({ file, reason: `File size exceeds ${maxSize / (1024 * 1024)}MB limit` });
        return;
      }
      
      // Check MIME type if accept is specified
      if (accept && accept.length > 0) {
        const fileType = file.type || '';
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const isAccepted = accept.some(pattern => {
          if (pattern.endsWith('/*')) {
            // Pattern like "image/*"
            const baseType = pattern.split('/')[0];
            return fileType.startsWith(baseType + '/');
          } else if (pattern.startsWith('.')) {
            // Pattern like ".pdf"
            return fileExtension === pattern;
          } else {
            // Exact MIME type
            return fileType === pattern;
          }
        });
        
        if (!isAccepted) {
          invalidFiles.push({ file, reason: `File type not allowed. Accepted: ${accept.join(', ')}` });
          return;
        }
      }
      
      validFiles.push(file);
    });
    
    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      // Add invalid files to queue with error status for display
      const errorItems = invalidFiles.map(({ file, reason }) => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        status: 'error' as const,
        progress: 0,
        error: reason,
      }));
      setUploadQueue(prev => [...prev, ...errorItems]);
    }
    
    // Add valid files to queue
    const newFiles = validFiles.map(file => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      status: 'pending' as const,
      progress: 0,
    }));
    
    setUploadQueue(prev => [...prev, ...newFiles]);
    
    // Start upload process for valid files
    newFiles.forEach(queueItem => {
      uploadFile(queueItem);
    });
  };
  
  const uploadFile = async (queueItem: { id: string; file: File; status: 'pending' | 'uploading' | 'done' | 'error'; progress: number }) => {
    try {
      // Update queue status
      setUploadQueue(prev => prev.map(item => 
        item.id === queueItem.id ? { ...item, status: 'uploading', progress: 10 } : item
      ));
      
      let uploadResult: { file_url: string; file_name: string };
      let attachResult: GetAttachmentsResponse | undefined;
      
      if (isUploadOnly) {
        // Upload-only mode: upload file without attaching to document
        uploadResult = await uploadFileUnattached(queueItem.file, {
          folder: 'Home/Uploads',
          isPrivate: false,
        });
        
        setUploadQueue(prev => prev.map(item => 
          item.id === queueItem.id ? { ...item, progress: 100 } : item
        ));
        
        // Create synthetic GetAttachmentsResponse for upload-only mode
        attachResult = {
          doctype: context.doctype,
          name: context.name,
          fieldname: null,
          attachments: [{
            docname: `temp-${Date.now()}-${Math.random()}`,
            file_name: uploadResult.file_name,
            file_url: uploadResult.file_url,
            is_private: 0,
            attached_to_field: null,
            creation: new Date().toISOString(),
          }],
        };
      } else {
        // Normal attach mode: upload and attach to document
        uploadResult = await erp.docs.uploadAttachment({
          doctype: context.doctype,
          name: context.name,
          file: queueItem.file,
        });
        
        setUploadQueue(prev => prev.map(item => 
          item.id === queueItem.id ? { ...item, progress: 50 } : item
        ));
        
        // Step 2: Attach to document
        attachResult = await attachFileMutation.mutateAsync({
          file_url: uploadResult.file_url,
          file_name: uploadResult.file_name,
        });
      }
      
      // Update queue - keep dialog open so user can see success
      setUploadQueue(prev => {
        const updated = prev.map(item => 
          item.id === queueItem.id 
            ? { ...item, status: 'done' as const, progress: 100, file_url: uploadResult.file_url, file_name: uploadResult.file_name }
            : item
        );
        
        const allDone = updated.every(item => item.status === 'done' || item.status === 'error');
        
        // Call onCompleted when all files are done
        if (allDone) {
          if (isUploadOnly) {
            // For upload-only mode with multiple files, aggregate all uploaded files
            const allUploadedFiles = updated
              .filter(item => item.status === 'done' && item.file_url && item.file_name)
              .map(item => ({
                docname: `temp-${Date.now()}-${Math.random()}`,
                file_name: item.file_name!,
                file_url: item.file_url!,
                is_private: 0,
                attached_to_field: null,
                creation: new Date().toISOString(),
              }));
            
            const aggregatedResult: GetAttachmentsResponse = {
              doctype: context.doctype,
              name: context.name,
              fieldname: null,
              attachments: allUploadedFiles,
            };
            
            setTimeout(() => {
              onCompleted?.(aggregatedResult);
            }, 100);
          } else if (attachResult) {
            // Normal attach mode: use the last attach result
            setTimeout(() => {
              onCompleted?.(attachResult);
            }, 100);
          }
        }
        
        return updated;
      });
    } catch (error) {
      setUploadQueue(prev => prev.map(item => 
        item.id === queueItem.id 
          ? { 
              ...item, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed' 
            }
          : item
      ));
    }
  };
  
  const removeFromQueue = (id: string) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };
  
  // ========== Library Tab Handlers ==========
  
  const handleLibraryFileSelect = async (file: FileLibraryItem) => {
    if (isUploadOnly) {
      // Upload-only mode: return file immediately without attaching
      const syntheticResult: GetAttachmentsResponse = {
        doctype: context.doctype,
        name: context.name,
        fieldname: null,
        attachments: [{
          docname: `temp-${Date.now()}-${Math.random()}`,
          file_name: file.file_name,
          file_url: file.file_url,
          is_private: 0,
          attached_to_field: null,
          creation: new Date().toISOString(),
        }],
      };
      onCompleted?.(syntheticResult);
      return;
    }
    
    if (!multiple) {
      // Single mode: attach immediately but keep dialog open
      try {
        const result = await attachFileMutation.mutateAsync({
          file_url: file.file_url,
          file_name: file.file_name,
        });
        onCompleted?.(result);
      } catch (error) {
        // Error is handled by mutation
      }
    } else {
      // Multiple mode: toggle selection
      setSelectedLibraryFiles(prev => {
        const next = new Set(prev);
        if (next.has(file.file_url)) {
          next.delete(file.file_url);
        } else {
          next.add(file.file_url);
        }
        return next;
      });
    }
  };
  
  const handleAttachSelected = async () => {
    if (selectedLibraryFiles.size === 0) return;
    
    const filesToAttach = libraryFiles.filter((f) => {
      const normalized = normalizeFileUrl(f.file_url) || f.file_url;
      return (
        selectedLibraryFiles.has(f.file_url) &&
        (!isUploadOnly || !attachedFileUrls.has(normalized))
      );
    });
    
    if (isUploadOnly) {
      // Upload-only mode: return selected files without attaching
      const syntheticResult: GetAttachmentsResponse = {
        doctype: context.doctype,
        name: context.name,
        fieldname: null,
        attachments: filesToAttach.map(file => ({
          docname: `temp-${Date.now()}-${Math.random()}`,
          file_name: file.file_name,
          file_url: file.file_url,
          is_private: 0,
          attached_to_field: null,
          creation: new Date().toISOString(),
        })),
      };
      onCompleted?.(syntheticResult);
      setSelectedLibraryFiles(new Set());
      return;
    }
    
    let lastResult: GetAttachmentsResponse | undefined;
    
    for (const file of filesToAttach) {
      try {
        lastResult = await attachFileMutation.mutateAsync({
          file_url: file.file_url,
          file_name: file.file_name,
        });
      } catch (error) {
        const msg = (error as any)?.message || (error as any)?.exc || String(error);
        console.error('[AttachmentPicker] Failed to attach file:', file.file_url, msg);
        toast.error(`Failed to attach ${file.file_name || 'file'}: ${msg}`);
      }
    }

    if (lastResult) {
      onCompleted?.(lastResult);
      setSelectedLibraryFiles(new Set());
    }
  };
  
  // ========== Link Tab Handlers ==========
  
  const isValidUrl = url.startsWith("http://") || url.startsWith("https://");
  
  const handleCreateLink = async () => {
    if (!isValidUrl) return;
    
    if (isUploadOnly) {
      // Upload-only mode: return link as synthetic attachment
      const syntheticResult: GetAttachmentsResponse = {
        doctype: context.doctype,
        name: context.name,
        fieldname: null,
        attachments: [{
          docname: `temp-${Date.now()}-${Math.random()}`,
          file_name: label || url,
          file_url: url,
          is_private: 0,
          attached_to_field: null,
          creation: new Date().toISOString(),
        }],
      };
      onCompleted?.(syntheticResult);
      setUrl("");
      setLabel("");
      return;
    }
    
    try {
      const result = await createLinkMutation.mutateAsync({
        url,
        label: label || undefined,
      });
      
      onCompleted?.(result);
      
      // Reset form, but keep dialog open so user can continue
      setUrl("");
      setLabel("");
    } catch (error) {
      // Error is handled by mutation
    }
  };
  
  // ========== Camera Tab Handlers ==========
  
  const startCamera = async () => {
    try {
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Prefer back camera
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          setCameraError('Camera permission denied. Please allow camera access.');
        } else if (error.name === 'NotFoundError') {
          setCameraError('No camera found on this device.');
        } else {
          setCameraError('Failed to access camera.');
        }
      }
    }
  };
  
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };
  
  const capturePhoto = async () => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      
      // Convert blob to File
      // File constructor is available in modern browsers
      const fileName = `capture_${Date.now()}.png`;
      const fileOptions: FilePropertyBag = { type: 'image/png' };
      const file = new File([blob], fileName, fileOptions);
      
      // Stop camera
      stopCamera();
      
      // Add to upload queue
      const queueItem = {
        id: `camera-${Date.now()}`,
        file,
        status: 'pending' as const,
        progress: 0,
      };
      
      setUploadQueue(prev => [...prev, queueItem]);
      await uploadFile(queueItem);
    }, 'image/png');
  };
  
  // Cleanup camera on unmount or tab change
  useEffect(() => {
    if (activeTab !== 'camera') {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [activeTab]);
  
  // Focus management - focus first tab when dialog opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure dialog is rendered
      setTimeout(() => {
        const firstTab = document.querySelector('[role="tablist"] [role="tab"]') as HTMLElement;
        firstTab?.focus();
      }, 100);
    }
  }, [open]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100">
            {mode === 'replace' ? 'Replace Attachment' : 'Attach Files'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Upload files from your device, browse the file library, or add a link
          </DialogDescription>
          {mode === 'replace' && context.fieldname && (
            <p className="text-sm text-muted-foreground mt-1">
              Uploading a new file will replace the current attachment for this field.
            </p>
          )}
        </DialogHeader>
        
        <Tabs 
          value={activeTab} 
          onValueChange={(v) => setActiveTab(v as any)} 
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className="mx-6 mb-4 h-auto p-1 bg-muted/50 rounded">
            <TabsTrigger 
              value="device" 
              className="flex items-center gap-2 data-[state=active]:bg-background"
            >
              <Upload className="h-4 w-4" />
              <span>My Device</span>
            </TabsTrigger>
            <TabsTrigger 
              value="library"
              className="flex items-center gap-2 data-[state=active]:bg-background"
            >
              <FolderOpen className="h-4 w-4" />
              <span>Library</span>
            </TabsTrigger>
            <TabsTrigger 
              value="link"
              className="flex items-center gap-2 data-[state=active]:bg-background"
            >
              <LinkIcon className="h-4 w-4" />
              <span>Link</span>
            </TabsTrigger>
            {isCameraSupported && (
              <TabsTrigger 
                value="camera"
                className="flex items-center gap-2 data-[state=active]:bg-background"
              >
                <Camera className="h-4 w-4" />
                <span>Camera</span>
              </TabsTrigger>
            )}
          </TabsList>
          
          <div className="flex-1 overflow-y-auto px-6 pb-6 min-h-0">
            {/* My Device Tab */}
            <TabsContent value="device" className="mt-0 space-y-4">
              {/* Drag & Drop Zone */}
              {!isTouchDevice && (
                <div
                  ref={dropZoneRef}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={cn(
                    "border-2 border-dashed rounded p-8 text-center transition-colors",
                    isDragging 
                      ? "border-[#007AFF] bg-[#007AFF]/5" 
                      : "border-border/40 hover:border-border/60"
                  )}
                >
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm font-medium mb-1">Drag and drop files here</p>
                  <p className="text-xs text-muted-foreground mb-4">or</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Browse Files
                  </Button>
                </div>
              )}
              
              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple={multiple}
                accept={acceptString}
                capture={acceptString?.includes("image") ? "environment" : undefined}
                onChange={(e) => handleFileSelect(e.target.files)}
                aria-label="Select files to upload"
              />
              
              {/* Mobile-friendly button */}
              {isTouchDevice && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-20 rounded border-2 border-dashed"
                >
                  <Upload className="h-6 w-6 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Choose Files</p>
                    <p className="text-xs text-muted-foreground">
                      {acceptString ? `Accepted: ${acceptString}` : 'All file types'}
                    </p>
                  </div>
                </Button>
              )}
              
              {/* Success Banner */}
              {allUploadsComplete && successfulUploads > 0 && (
                <div className="rounded bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-8 w-8 rounded-full bg-green-600 flex items-center justify-center">
                      <Check className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                      {successfulUploads} file{successfulUploads > 1 ? 's' : ''} uploaded successfully
                    </p>
                    {failedUploads > 0 && (
                      <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                        {failedUploads} file{failedUploads > 1 ? 's' : ''} failed to upload
                      </p>
                    )}
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      Files are now attached to this document
                    </p>
                  </div>
                </div>
              )}
              
              {/* Upload Queue */}
              {uploadQueue.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">Upload Queue</h3>
                    {allUploadsComplete && successfulUploads > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {successfulUploads} of {uploadQueue.length} complete
                      </span>
                    )}
                  </div>
                  {uploadQueue.map((item) => {
                    const isDone = item.status === 'done';
                    
                    return (
                    <div
                      key={item.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded border transition-all duration-200",
                          isDone
                            ? "bg-green-50/50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                            : item.status === 'error'
                            ? "bg-destructive/5 border-destructive/20"
                            : "bg-muted/30 border-border/30"
                        )}
                    >
                      <div className="flex-shrink-0">
                          {isDone ? (
                            <div className="h-10 w-10 rounded-sm bg-green-600 flex items-center justify-center">
                              <Check className="h-5 w-5 text-white" />
                            </div>
                        ) : item.status === 'error' ? (
                            <div className="h-10 w-10 rounded-sm bg-destructive/10 flex items-center justify-center">
                          <AlertCircle className="h-5 w-5 text-destructive" />
                            </div>
                        ) : item.status === 'uploading' ? (
                            <div className="h-10 w-10 rounded-sm bg-[#007AFF]/10 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 text-[#007AFF] animate-spin" />
                            </div>
                        ) : (
                            <div className="h-10 w-10 rounded-sm bg-muted flex items-center justify-center">
                          <FileIcon className="h-5 w-5 text-muted-foreground" />
                            </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium truncate">{item.file.name}</p>
                            {isDone && (
                              <span className="flex-shrink-0 text-[10px] font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/50 px-2 py-0.5 rounded-full">
                                Uploaded
                              </span>
                            )}
                          </div>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(item.file.size)}
                        </p>
                        {item.status === 'uploading' && (
                          <Progress value={item.progress} className="mt-2" />
                        )}
                        {item.status === 'error' && item.error && (
                          <p className="text-xs text-destructive mt-1">{item.error}</p>
                        )}
                          {isDone && item.file_url && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✓ Successfully attached
                            </p>
                        )}
                      </div>
                      {(item.status === 'error' || item.status === 'pending') && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromQueue(item.id)}
                          className="h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
              
              {/* Done Button - Show when all uploads complete */}
              {allUploadsComplete && successfulUploads > 0 && (
                <div className="pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                    }}
                    className="w-full rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Done
                  </Button>
                </div>
              )}
            </TabsContent>
            
            {/* Library Tab */}
            <TabsContent value="library" className="mt-0 space-y-4">
              {/* Search */}
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded"
              />
              
              {/* File Grid */}
              {isLoadingLibrary ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : libraryFiles.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery ? 'No files found' : 'No files in library'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {libraryFiles.map((file) => {
                      // Normalize file URL for comparison (handles absolute vs relative URLs)
                      const normalizedFileUrl = normalizeFileUrl(file.file_url) || file.file_url;
                      const isAttached = attachedFileUrls.has(normalizedFileUrl);
                      const isSelected = selectedLibraryFiles.has(file.file_url);
                      const isImage = isImageFile(file);
                      const isVideo = isVideoFile(file);
                      
                      return (
                        <button
                          key={file.docname}
                          type="button"
                          onClick={() => !isAttached && handleLibraryFileSelect(file)}
                          disabled={isAttached || attachFileMutation.isPending}
                          className={cn(
                            "relative aspect-square rounded overflow-hidden border-2 transition-all",
                            isAttached 
                              ? "border-muted opacity-50 cursor-not-allowed"
                              : isSelected
                              ? "border-[#007AFF] ring-2 ring-[#007AFF]/20"
                              : "border-border/40 hover:border-border/60 cursor-pointer",
                            attachFileMutation.isPending && "opacity-50"
                          )}
                        >
                          {isImage ? (
                            <ImageWithFallback
                              src={file.file_url}
                              alt={file.file_name}
                            />
                          ) : isVideo ? (
                            <div className="relative w-full h-full bg-gray-900 dark:bg-gray-800 flex items-center justify-center">
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Play className="h-12 w-12 text-white/80" />
                              </div>
                              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded-md">
                                VIDEO
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <FileIcon className="h-8 w-8 text-muted-foreground" />
                            </div>
                          )}
                          
                          {/* Status indicators */}
                          {isAttached && (
                            <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] px-2 py-1 rounded-full font-medium">
                              Attached
                            </div>
                          )}
                          {isSelected && !isAttached && (
                            <div className="absolute top-2 right-2 bg-[#007AFF] text-white rounded-full p-1">
                              <Check className="h-3 w-3" />
                            </div>
                          )}
                          
                          {/* File name overlay */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">
                            {file.file_name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Load More / Pagination */}
                  {hasNextPage && (
                    <div className="flex justify-center pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                        className="rounded-full"
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Load More'
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Attach Selected Button (multiple mode) */}
                  {multiple && selectedLibraryFiles.size > 0 && (
                    <div className="sticky bottom-0 pt-4 border-t bg-background">
                      <Button
                        type="button"
                        onClick={handleAttachSelected}
                        disabled={attachFileMutation.isPending}
                        className="w-full rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white"
                      >
                        {attachFileMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Attaching...
                          </>
                        ) : (
                          `Attach ${selectedLibraryFiles.size} file${selectedLibraryFiles.size > 1 ? 's' : ''}`
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
            
            {/* Link Tab */}
            <TabsContent value="link" className="mt-0 space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">URL</label>
                  <Input
                    type="url"
                    placeholder="https://example.com/file.pdf"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="rounded"
                    aria-label="URL for link attachment"
                  />
                  {url && !isValidUrl && (
                    <p className="text-xs text-destructive mt-1">
                      URL must start with http:// or https://
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Display Name (optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="File name or description"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="rounded"
                    aria-label="Display name for link attachment"
                  />
                </div>
                
                <Button
                  type="button"
                  onClick={handleCreateLink}
                  disabled={!isValidUrl || createLinkMutation.isPending}
                  className="w-full rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white"
                >
                  {createLinkMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Link Attachment'
                  )}
                </Button>
              </div>
            </TabsContent>
            
            {/* Camera Tab */}
            <TabsContent value="camera" className="mt-0 space-y-4">
              {!isCameraSupported ? (
                <div className="text-center py-12">
                  <Camera className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm font-medium mb-2">Camera not supported</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Your device or browser doesn't support camera access.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setActiveTab('device');
                      fileInputRef.current?.click();
                    }}
                    className="rounded-full"
                  >
                    Use My Device Instead
                  </Button>
                </div>
              ) : cameraError ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
                  <p className="text-sm font-medium mb-2">Camera Error</p>
                  <p className="text-xs text-muted-foreground mb-4">{cameraError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCameraError(null);
                      startCamera();
                    }}
                    className="rounded-full"
                  >
                    Try Again
                  </Button>
                </div>
              ) : !stream ? (
                <div className="text-center py-12">
                  <Camera className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-sm font-medium mb-4">Camera Ready</p>
                  <Button
                    type="button"
                    onClick={startCamera}
                    className="rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Start Camera
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative aspect-video rounded overflow-hidden bg-black">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={stopCamera}
                      className="flex-1 rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={capturePhoto}
                      className="flex-1 rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Capture
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
